import React, { useState } from 'react';

import { useNavigate, useLocation } from 'react-router-dom';

import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import AuthLayout from '../../components/layout/AuthLayout';
import { useCommunicationApi } from '../../hooks/useCommunicationApi';

const Unsubscribe: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUnsubscribed, setIsUnsubscribed] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { unsubscribeEmail } = useCommunicationApi();

  // Extract the token from the URL query string
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  if (!token) {
    return (
      <AuthLayout
        title="Unsubscribe"
        description="We're sorry, but we couldn't process your request because the token is missing."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            Please check the link you received or contact support for
            assistance.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go Back to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    try {
      setIsLoading(true);
      const response = await unsubscribeEmail({ token, reason });

      if (response && response.success) {
        setIsUnsubscribed(true);
      } else {
        setErrorMessage(
          'We encountered an issue processing your request. Please try again later.',
        );
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setErrorMessage('An unexpected error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isUnsubscribed) {
    return (
      <AuthLayout
        title="Unsubscribed Successfully"
        description="Thank you for letting us know. You have been unsubscribed from further emails."
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center">
            If you change your mind, you can resubscribe at any time.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/')} className="w-full">
              Go Back to Home
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Unsubscribe"
      description="We're sorry to see you go. Please let us know why you're unsubscribing."
    >
      <div className="mt-8">
        {errorMessage && (
          <div className="mb-4 text-sm text-red-600">{errorMessage}</div>
        )}
        <form onSubmit={handleSubmit}>
          <FormField
            id="reason"
            label="Reason for Unsubscribing (optional)"
            type="textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Let us know why you're unsubscribing"
          />
          <div className="mt-6">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              isLoading={isLoading}
            >
              Unsubscribe
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Unsubscribe;
